import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format, isToday, isTomorrow, addDays, subDays, isSameDay } from 'date-fns';
import VarietySelector from './VarietySelector';

const { FiCalendar, FiPackage, FiTruck, FiAlertCircle, FiCheckCircle, FiImage, FiTrash2, FiPlus, FiX, FiLayers } = FiIcons;

const ManualSowModal = ({ isOpen, onClose, varieties, onSave }) => {
    const [formData, setFormData] = useState({
        sowingDate: format(new Date(), 'yyyy-MM-dd'),
        isRecurring: false,
        intervalWeeks: 1,
        endDate: '',
        items: [{ varietyId: '', quantity: '', bufferQuantity: '' }]
    });

    if (!isOpen) return null;

    const handleAddItem = () => {
        setFormData(prev => ({
            ...prev,
            items: [...prev.items, { varietyId: '', quantity: '', bufferQuantity: '' }]
        }));
    };

    const handleRemoveItem = (index) => {
        if (formData.items.length <= 1) return;
        setFormData(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...formData.items];
        newItems[index][field] = value;
        setFormData(prev => ({ ...prev, items: newItems }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // Transform for single item legacy support if needed, or update handleManualSow to accept items
        // Current handleManualSow in SowingManager expects: 
        // data.varietyId, data.quantity. 
        // We NEED to update handleManualSow TO SUPPORT MULTIPLE ITEMS or we just loop here.
        // The prompt implies "add in choices with qty and buffer qty", suggesting multi-select.

        // Let's pass the raw formData with items to onSave. 
        // We will need to update handleManualSow in SowingManager next.
        onSave(formData);
    };

    // Filter varieties for manual sowing (Lettuce/Flowers usually, but maybe Microgreens too if needed?)
    // The previous code filtered out Microgreens. Let's keep that filter but using VarietySelector logic?
    // User wants "choices", implies selection. VarietySelector is good.
    // Let's assume all varieties except maybe those explicitly hidden.
    // Dashboard previously filtered: varieties.filter(v => v.category !== 'Microgreens')
    // const manualVarieties = varieties.filter(v => v.category !== 'Microgreens'); // Removed as per instruction

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl overflow-hidden animate-fade-in-up flex flex-col h-[80vh]">
                <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 flex-shrink-0">
                    <div>
                        <h3 className="text-xl font-bold text-[#1e3a8a]">Manual Sowing Task</h3>
                        <p className="text-xs text-gray-400 mt-0.5">Create one-off or recurring sowing production tasks</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-red-500 transition-colors bg-white p-2 rounded-lg border border-gray-100 shadow-sm">
                        <SafeIcon icon={FiX} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6 pb-64">
                    {/* Date & Recurring Toggle */}
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Sowing Date</label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"><SafeIcon icon={FiCalendar} /></span>
                                    <input
                                        type="date"
                                        required
                                        className="w-full bg-gray-50 border border-gray-200 text-gray-800 text-sm rounded-xl pl-10 pr-4 py-3 outline-none focus:border-[#1e3a8a] focus:ring-1 focus:ring-[#1e3a8a] transition-all font-medium"
                                        value={formData.sowingDate}
                                        onChange={e => setFormData({ ...formData, sowingDate: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div className="flex flex-col justify-end pb-3">
                                <label className="flex items-center space-x-3 cursor-pointer select-none bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 hover:border-blue-200 transition-colors">
                                    <input
                                        type="checkbox"
                                        className="w-5 h-5 accent-[#1e3a8a] cursor-pointer"
                                        checked={formData.isRecurring}
                                        onChange={e => setFormData({ ...formData, isRecurring: e.target.checked })}
                                    />
                                    <div>
                                        <span className="block text-sm font-bold text-gray-800 leading-none">Repeat this schedule</span>
                                    </div>
                                </label>
                            </div>
                        </div>

                        {/* Recurring Details (Conditional) */}
                        {formData.isRecurring && (
                            <div className="grid grid-cols-2 gap-6 bg-blue-50/50 p-4 rounded-xl border border-blue-100 animate-fade-in">
                                <div>
                                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Repeat Every (Weeks)</label>
                                    <input
                                        type="number"
                                        min="1"
                                        required={formData.isRecurring}
                                        className="w-full bg-white border border-gray-200 text-gray-800 text-sm rounded-xl px-4 py-2.5 outline-none focus:border-[#1e3a8a]"
                                        value={formData.intervalWeeks}
                                        onChange={e => setFormData({ ...formData, intervalWeeks: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">End Date</label>
                                    <input
                                        type="date"
                                        className="w-full bg-white border border-gray-200 text-gray-800 text-sm rounded-xl px-4 py-2.5 outline-none focus:border-[#1e3a8a]"
                                        value={formData.endDate}
                                        onChange={e => setFormData({ ...formData, endDate: e.target.value })}
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Items Section */}
                    <div>
                        <div className="flex justify-between items-center mb-3">
                            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Varieties to Sow</label>
                            <button
                                type="button"
                                onClick={handleAddItem}
                                className="text-[#16a34a] hover:text-green-700 text-xs font-bold flex items-center space-x-1 py-1 px-2 rounded-lg hover:bg-green-50 transition-colors"
                            >
                                <SafeIcon icon={FiPlus} />
                                <span>Add Variety</span>
                            </button>
                        </div>

                        <div className="space-y-3">
                            {formData.items.map((item, index) => (
                                <div key={index} className="grid grid-cols-12 gap-3 items-start bg-gray-50/50 p-3 rounded-xl border border-gray-100 hover:border-gray-200 transition-colors relative group">
                                    <div className="col-span-12 md:col-span-6">
                                        <label className="block text-[10px] font-medium text-gray-400 uppercase tracking-widest mb-1">Variety</label>
                                        <div className="w-full">
                                            <VarietySelector
                                                varieties={varieties}
                                                value={item.varietyId}
                                                onChange={(val) => handleItemChange(index, 'varietyId', val)}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-span-12 sm:col-span-6 md:col-span-2">
                                        <label className="block text-[10px] font-medium text-gray-400 uppercase tracking-widest mb-1">Target</label>
                                        <input
                                            type="number"
                                            placeholder="Qty"
                                            required
                                            min="0"
                                            className="w-full bg-white border border-gray-200 text-gray-800 text-sm rounded-lg px-3 py-2 outline-none focus:border-[#1e3a8a] text-center font-medium"
                                            value={item.quantity}
                                            onChange={e => handleItemChange(index, 'quantity', e.target.value)}
                                        />
                                    </div>
                                    <div className="col-span-12 sm:col-span-6 md:col-span-2">
                                        <label className="block text-[10px] font-medium text-orange-400 uppercase tracking-widest mb-1">Buffer</label>
                                        <input
                                            type="number"
                                            placeholder="Buff"
                                            min="0"
                                            className="w-full bg-white border border-orange-100 text-orange-600 text-sm rounded-lg px-3 py-2 outline-none focus:border-orange-500 text-center font-medium placeholder-orange-200"
                                            value={item.bufferQuantity}
                                            onChange={e => handleItemChange(index, 'bufferQuantity', e.target.value)}
                                        />
                                    </div>
                                    <div className="col-span-12 md:col-span-2 flex items-end justify-center h-full pb-2 md:pt-0 pt-2">
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveItem(index)}
                                            className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                            disabled={formData.items.length === 1}
                                        >
                                            <SafeIcon icon={FiTrash2} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </form>

                <div className="p-6 border-t border-gray-100 bg-gray-50/50 flex space-x-3 flex-shrink-0">
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex-1 px-4 py-3 bg-white border border-gray-200 text-gray-600 rounded-xl text-sm font-bold hover:bg-gray-50 transition-all shadow-sm"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        className="flex-1 px-4 py-3 bg-[#1e3a8a] text-white rounded-xl text-sm font-bold hover:bg-blue-900 shadow-lg shadow-blue-100 transition-all active:scale-95"
                    >
                        {formData.isRecurring ? 'Create Recurring Schedule' : 'Create Tasks'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const EditTaskModal = ({ isOpen, onClose, order, onSave, varieties }) => {
    const [formData, setFormData] = useState(null);

    React.useEffect(() => {
        if (order) {
            setFormData({
                ...order,
                baseDate: format(new Date(order.baseDate), 'yyyy-MM-dd'),
                items: order.items.map(i => ({ ...i }))
            });
        }
    }, [order]);

    if (!isOpen || !formData) return null;

    const v = varieties.find(va => va.id.toString() === formData.items[0]?.varietyId.toString());

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden animate-fade-in-up">
                <div className="p-5 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                    <h3 className="text-lg font-light text-[#1e3a8a]">Edit Task</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-red-500"><SafeIcon icon={FiX} /></button>
                </div>
                <form onSubmit={(e) => { e.preventDefault(); onSave(formData); }} className="p-5 space-y-4">
                    <div className="bg-blue-50/50 p-3 rounded-xl border border-blue-100 text-xs text-blue-800 font-medium text-center">
                        {v?.name || 'Unknown Variety'}
                    </div>
                    <div>
                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Date</label>
                        <input type="date" value={formData.baseDate} onChange={e => setFormData({ ...formData, baseDate: e.target.value })} className="w-full bg-gray-50 border border-gray-200 text-gray-800 text-sm rounded-xl px-4 py-3 outline-none focus:border-[#16a34a] focus:ring-1 focus:ring-[#16a34a] transition-all" required />
                    </div>
                    {formData.items.map((item, idx) => (
                        <div key={idx}>
                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Qty</label>
                            <input type="number" value={item.quantity} onChange={e => {
                                const newItems = [...formData.items];
                                newItems[idx].quantity = e.target.value;
                                setFormData({ ...formData, items: newItems });
                            }} className="w-full bg-gray-50 border border-gray-200 text-gray-800 text-sm rounded-xl px-4 py-3 outline-none focus:border-[#16a34a] focus:ring-1 focus:ring-[#16a34a] transition-all" />
                        </div>
                    ))}
                    <button type="submit" className="w-full px-4 py-3 bg-[#1e3a8a] text-white rounded-xl text-sm font-bold hover:bg-blue-900 shadow-lg shadow-blue-100 transition-all mt-2">Save Changes</button>
                </form>
            </div>
        </div>
    );
};

const SowingManager = ({ orders, varieties, batches, onUpdate }) => {
    const today = new Date();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [viewMode, setViewMode] = useState('day'); // 'day' | 'all'
    const [editingOrder, setEditingOrder] = useState(null);
    const [selectedTasks, setSelectedTasks] = useState([]);

    const toggleTask = (taskId) => {
        setSelectedTasks(prev =>
            prev.includes(taskId) ? prev.filter(id => id !== taskId) : [...prev, taskId]
        );
    };

    // 1. Calculate production tasks (same as Dashboard)
    const productionTasks = orders.flatMap(order => {
        // Skip sowing tasks for Sudden orders (they come from stock)
        if (order.isSudden) {
            return []; // Sudden orders don't have sowing tasks in this context
        }

        return order.items.map((item, itemIdx) => {
            const variety = varieties.find(v => v.id === item.varietyId);
            if (!variety) return null;

            // Exclude non-sowing categories from task generation
            const isManualSow = order.customer === 'Stock (Manual)';
            const category = (variety.category || '').toLowerCase();

            // Edible Flowers are allowed for Manual Sowing (seeds/pcs), otherwise excluded (grams)
            if (!isManualSow && category === 'edible flowers') return null;

            // Lettuce/Leaves are excluded for customer orders (buffer fulfillment), but valid for Manual Sow
            if (!isManualSow && ['lettuce', 'edible leaves'].includes(category)) {
                return null;
            }

            const base = new Date(order.baseDate);
            const growingDays = variety.growingDays || 0;
            const darkDays = variety.darkTreatmentDays || 0;
            const soakDays = variety.needSoak ? parseFloat(variety.soakDuration || 0) : 0;
            const germinationDays = (category !== 'microgreens' && category !== 'microgreen') ? darkDays : 0; // For plants, 'darkTreatment' is germination
            const totalCycle = growingDays + darkDays; // Note: soak is pre-sow usually, or part of cycle? Dashboard ignores it.

            // Standardizing: Delivery Date is anchor.
            let sowingDate, deliveryDate, soakDate;

            if (order.dateType === 'delivery') {
                deliveryDate = base;
                sowingDate = subDays(base, totalCycle);
            } else {
                sowingDate = base; // "Sowing" means put on shelf/media
                deliveryDate = addDays(base, totalCycle);
            }

            soakDate = subDays(sowingDate, soakDays);

            const taskId = `${order.id} -${item.varietyId} -${itemIdx} `;
            const batch = batches.find(b => b.taskId === taskId);

            // Unveil Date (for Microgreens): Sowing Date (Actual if batch exists) + Dark Days
            let unveilDate = null;
            if (category === 'microgreens' || category === 'microgreen') {
                const sowRef = batch ? new Date(batch.actualSowingDate) : sowingDate;
                unveilDate = addDays(sowRef, darkDays);
            }

            // Transplant Date (for Plants): Sowing Date (Actual) + Germination Days
            let transplantDate = null;
            if (category !== 'microgreens' && category !== 'microgreen') {
                const sowRef = batch ? new Date(batch.actualSowingDate) : sowingDate;
                transplantDate = addDays(sowRef, germinationDays);
            }

            return {
                taskId,
                orderId: order.id,
                customer: order.customer,
                variety,
                quantity: item.quantity,
                bufferQuantity: item.bufferQuantity || 0,
                sowingDate,
                soakDate,
                unveilDate,
                transplantDate,
                harvestDate: deliveryDate,
                deliveryDate,
                batch,
                isSudden: false
            };
        }).filter(Boolean);
    });

    // 2. Filter Operations
    const filterByDate = (date) => {
        if (!date) return false;
        if (viewMode === 'all') return true;
        return isSameDay(new Date(date), selectedDate);
    };

    // A. Microgreens to Soak
    // Condition: Microgreen + Need Soak + !Batch + SoakDate match
    const microgreensToSoak = productionTasks.filter(t => {
        const cat = (t.variety.category || '').toLowerCase();
        return (cat === 'microgreens' || cat === 'microgreen') &&
            t.variety.needSoak &&
            !t.batch &&
            filterByDate(t.soakDate);
    });

    // B. Microgreens to Do (Sow)
    // Condition: Microgreen + !Batch + SowDate match
    const microgreensToSow = productionTasks.filter(t => {
        const cat = (t.variety.category || '').toLowerCase();
        return (cat === 'microgreens' || cat === 'microgreen') &&
            !t.batch &&
            filterByDate(t.sowingDate);
    });

    // C. Microgreens to Unveil
    // Condition: Microgreen + Batch Exists + UnveilDate match
    // Note: If viewMode is 'all', showing ALL unveiled might be too much. 
    // Maybe show active batches in dark phase? 
    // For now, adhere to filterByDate logic for "Operations to do Today".
    const microgreensToUnveil = productionTasks.filter(t => {
        const cat = (t.variety.category || '').toLowerCase();
        return (cat === 'microgreens' || cat === 'microgreen') &&
            t.batch &&
            filterByDate(t.unveilDate);
    });

    // D. Plants to Sow
    // Condition: !Microgreen + !Batch + SowDate match
    const plantsToSow = productionTasks.filter(t => {
        const cat = (t.variety.category || '').toLowerCase();
        return (cat !== 'microgreens' && cat !== 'microgreen') &&
            !t.batch && // Ensure category allow-list in productionTasks includes these
            filterByDate(t.sowingDate);
    });

    // E. Plants to Transplant
    // Condition: !Microgreen + Batch Exists + TransplantDate match
    const plantsToTransplant = productionTasks.filter(t => {
        const cat = (t.variety.category || '').toLowerCase();
        return (cat !== 'microgreens' && cat !== 'microgreen') &&
            t.batch &&
            filterByDate(t.transplantDate);
    });


    const markTaskComplete = async (taskId, isDelivered = false) => {
        try {
            await fetch('/api/batches', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    taskId,
                    actualSowingDate: new Date().toISOString(),
                    delivered: isDelivered ? 1 : 0
                })
            });
            if (onUpdate) onUpdate();
        } catch (error) {
            console.error('Task update failed:', error);
        }
    };

    const handleDeleteOrder = async (orderId) => {
        if (!window.confirm('Delete this order?')) return;
        try {
            await fetch(`/api/orders/${orderId}`, { method: 'DELETE' });
            if (onUpdate) onUpdate();
        } catch (error) {
            console.error('Delete failed:', error);
        }
    };

    const handleSaveEdit = async (order) => {
        try {
            const res = await fetch(`/api/orders/${order.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(order)
            });
            if (res.ok) {
                setEditingOrder(null);
                if (onUpdate) onUpdate();
            }
        } catch (error) {
            console.error('Update failed:', error);
        }
    };

    const handleManualSow = async (data) => {
        try {
            // "data" now contains "items" array from the updated Modal
            const itemsPayload = data.items ? data.items.map(item => ({
                varietyId: item.varietyId,
                quantity: item.quantity,
                bufferQuantity: item.bufferQuantity || 0
            })) : [{
                varietyId: data.varietyId,
                quantity: data.quantity,
                bufferQuantity: 0
            }]; // Fallback for safety

            // Validate that we have valid items
            if (itemsPayload.some(i => !i.varietyId || !i.quantity)) {
                alert("Please ensure all items have a variety and quantity selected.");
                return;
            }

            const payload = {
                customer: 'Stock (Manual)',
                customerId: null,
                dateType: 'sowing',
                baseDate: data.sowingDate,
                isRecurring: data.isRecurring,
                isSudden: false,
                notes: data.isRecurring ? 'Manual Recurring Sowing' : 'Manual Sowing Task',
                items: itemsPayload,
                priceType: 'Standard'
            };

            if (data.isRecurring) {
                payload.intervalDays = parseInt(data.intervalWeeks) * 7;
                payload.endDate = data.endDate || null;
            }

            const endpoint = data.isRecurring ? '/api/recurring-rules' : '/api/orders';

            const res = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                setIsModalOpen(false);
                if (onUpdate) onUpdate();
            } else {
                const err = await res.json();
                alert("Failed to create task: " + (err.error || 'Unknown error'));
            }
        } catch (error) {
            console.error('Manual sow failed:', error);
            alert("Network error occurred.");
        }
    };

    const handleBatchDelete = async (ids) => {
        const orderIds = new Set();
        ids.forEach(tid => {
            const task = productionTasks.find(t => t.taskId === tid);
            if (task) orderIds.add(task.orderId);
        });

        if (!window.confirm(`Delete ${orderIds.size} orders ? `)) return;

        try {
            await Promise.all([...orderIds].map(oid =>
                fetch(`/api/orders/${oid}`, { method: 'DELETE' })
            ));
            setSelectedTasks(prev => prev.filter(id => !ids.includes(id)));
            if (onUpdate) onUpdate();
        } catch (error) {
            console.error('Batch delete failed:', error);
        }
    };

    const handleBatchSow = async (ids) => {
        if (!window.confirm(`Mark ${ids.length} tasks as sown ? `)) return;
        try {
            for (const tid of ids) {
                await markTaskComplete(tid, false);
            }
            setSelectedTasks(prev => prev.filter(id => !ids.includes(id)));
        } catch (e) { console.error(e); }
    };

    const TaskList = ({ title, tasks, emptyMsg, type }) => {
        if (tasks.length === 0) return null; // Or show empty state? User wants sections, maybe show Header + Empty

        return (
            <div className="mb-8 animate-fade-in-up">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-[#1e3a8a] flex items-center gap-2">
                        {title}
                        <span className="bg-blue-100 text-blue-800 text-xs font-bold px-2 py-0.5 rounded-full">{tasks.length}</span>
                    </h3>

                    <button
                        onClick={() => {
                            const ids = tasks.map(t => t.taskId);
                            const allSelected = ids.every(id => selectedTasks.includes(id));
                            if (allSelected) {
                                setSelectedTasks(prev => prev.filter(id => !ids.includes(id)));
                            } else {
                                setSelectedTasks(prev => [...new Set([...prev, ...ids])]);
                            }
                        }}
                        className="text-xs font-bold text-gray-400 hover:text-[#1e3a8a] transition-colors"
                    >
                        {tasks.every(t => selectedTasks.includes(t.taskId)) ? 'Deselect All' : 'Select All'}
                    </button>
                </div>
                <div className="space-y-4">
                    {tasks.map(task => (
                        <div key={task.taskId} className={`group flex flex - col sm: flex - row items - start p - 4 rounded - xl border transition - all ${selectedTasks.includes(task.taskId) ? 'border-blue-200 bg-blue-50/30 ring-1 ring-blue-100' : 'bg-white border-gray-100 hover:border-[#16a34a]/30 hover:shadow-md'} `}>
                            {/* Checkbox (Only for Sow tasks usually, but generic here) */}
                            <div className="mr-4 pt-1 mb-2 sm:mb-0">
                                <input type="checkbox"
                                    checked={selectedTasks.includes(task.taskId)}
                                    onChange={() => toggleTask(task.taskId)}
                                    className="w-4 h-4 rounded border-gray-300 text-[#1e3a8a] focus:ring-[#1e3a8a] cursor-pointer"
                                />
                            </div>

                            {/* Image & Main Info */}
                            <div className="flex items-center w-full sm:w-auto mb-4 sm:mb-0">
                                <div className="w-16 h-16 rounded-lg bg-white flex-shrink-0 mr-4 overflow-hidden border border-gray-100 mt-1 shadow-sm">
                                    {task.variety.image ? (
                                        <img src={task.variety.image} alt={task.variety.name} className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-gray-300">
                                            <SafeIcon icon={FiImage} className="text-2xl" />
                                        </div>
                                    )}
                                </div>
                                <div className="flex-1">
                                    <span className="text-lg font-bold text-gray-800 block">{task.variety.name}</span>
                                    <div className="flex items-center space-x-2 mt-1">
                                        <span className="text-xs font-bold text-[#16a34a] bg-green-50 px-2 py-0.5 rounded-full">
                                            {task.customer === 'Stock (Manual)' ? 'Qty: ' : 'O: '}{task.quantity}
                                        </span>
                                        {task.bufferQuantity > 0 && (
                                            <span className="text-xs font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">B: {task.bufferQuantity}</span>
                                        )}
                                    </div>
                                    <p className="text-[10px] text-gray-400 mt-1">{task.customer}</p>
                                </div>
                            </div>

                            {/* Details Grid */}
                            <div className="flex-1 min-w-0 sm:ml-24 sm:mr-4 w-full mt-2 sm:mt-0">
                                <div className="grid grid-cols-2 gap-12 text-xs text-gray-600 bg-gray-50/50 p-3 px-6 rounded-lg border border-gray-100/50">
                                    <div>
                                        <span className="block text-[10px] text-gray-400 uppercase tracking-widest mb-0.5">Target</span>
                                        <span className="font-bold text-gray-700 text-sm">{type === 'soak' ? format(new Date(task.soakDate), 'dd/MM') :
                                            type === 'unveil' ? format(new Date(task.unveilDate), 'dd/MM') :
                                                type === 'transplant' ? format(new Date(task.transplantDate), 'dd/MM') :
                                                    format(new Date(task.sowingDate), 'dd/MM')}</span>
                                    </div>
                                    <div>
                                        <span className="block text-[10px] text-gray-400 uppercase tracking-widest mb-0.5">Seed</span>
                                        <span className="font-bold text-[#1e3a8a] text-sm">
                                            {(() => {
                                                const totalQty = parseFloat(task.quantity || 0) + parseFloat(task.bufferQuantity || 0);
                                                const category = (task.variety.category || '').toLowerCase();

                                                // For Lettuce/Flowers/Leaves, we don't calculate seed weight for tasks usually (it's unit based)
                                                // User requested "-" instead of "0.0 g"
                                                if (['edible flowers', 'lettuce', 'edible leaves'].includes(category)) {
                                                    return '-';
                                                }

                                                const gram = parseFloat(task.variety.seedQuantityGram || 0);
                                                if (!gram) return '-';

                                                return `${(totalQty * gram).toFixed(1)} g`;
                                            })()}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex flex-row gap-2 mt-4 sm:mt-0 sm:ml-4">
                                {type === 'sow' && !task.batch && (
                                    <button
                                        onClick={() => markTaskComplete(task.taskId)}
                                        className="bg-[#16a34a] text-white hover:bg-green-700 px-5 py-2 rounded-lg text-xs font-bold transition-all shadow-sm active:scale-95"
                                    >
                                        Sow
                                    </button>
                                )}
                                {type === 'soak' && (
                                    <button className="bg-blue-100 text-blue-700 px-5 py-2 rounded-lg text-xs font-bold whitespace-nowrap">
                                        Ready
                                    </button>
                                )}
                                {(type === 'unveil' || type === 'transplant') && (
                                    <button className="bg-purple-100 text-purple-700 px-5 py-2 rounded-lg text-xs font-bold whitespace-nowrap">
                                        Due Now
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="p-6 md:p-10 max-w-7xl mx-auto pb-24 md:pb-10">
            <ManualSowModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                varieties={varieties}
                onSave={handleManualSow}
            />
            <EditTaskModal
                isOpen={!!editingOrder}
                onClose={() => setEditingOrder(null)}
                order={editingOrder}
                onSave={handleSaveEdit}
                varieties={varieties}
            />

            <div className="mb-8 md:mb-12 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center space-x-4">
                    <div className="bg-blue-50 p-3 rounded-2xl">
                        <SafeIcon icon={FiLayers} className="text-2xl text-[#1e3a8a]" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-bold text-[#1e3a8a] tracking-tight">Daily Operations</h1>
                        <p className="text-gray-500 text-sm mt-1">Manage sowing, soaking, and maintenance tasks</p>
                    </div>
                </div>

                <div className="flex items-center bg-gray-100/50 p-1 rounded-xl">
                    <button
                        onClick={() => setViewMode('day')}
                        className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${viewMode === 'day' ? 'bg-white text-[#1e3a8a] shadow-sm ring-1 ring-gray-200' : 'text-gray-400 hover:text-gray-600'}`}
                    >
                        Day View
                    </button>
                    <button
                        onClick={() => setViewMode('all')}
                        className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${viewMode === 'all' ? 'bg-white text-[#1e3a8a] shadow-sm ring-1 ring-gray-200' : 'text-gray-400 hover:text-gray-600'}`}
                    >
                        Pending All
                    </button>
                </div>
            </div>

            {/* Date Nav */}
            <div className="flex items-center justify-between bg-white p-4 rounded-2xl border border-gray-100 shadow-sm mb-8 sticky top-20 z-20">
                <button onClick={() => setSelectedDate(subDays(selectedDate, 1))} className="p-2 hover:bg-gray-50 rounded-lg text-gray-400 hover:text-[#1e3a8a] transition-all">
                    <SafeIcon icon={FiIcons.FiChevronLeft} className="text-xl" />
                </button>
                <div className="flex flex-col items-center">
                    <div className="flex items-center space-x-2">
                        <SafeIcon icon={FiCalendar} className="text-[#16a34a]" />
                        <span className="text-lg font-bold text-gray-800">
                            {isToday(selectedDate) ? 'Today' : isTomorrow(selectedDate) ? 'Tomorrow' : format(selectedDate, 'EEEE')}
                        </span>
                    </div>
                    <span className="text-xs font-medium text-gray-400">{format(selectedDate, 'dd MMMM yyyy')}</span>
                </div>
                <button onClick={() => setSelectedDate(addDays(selectedDate, 1))} className="p-2 hover:bg-gray-50 rounded-lg text-gray-400 hover:text-[#1e3a8a] transition-all">
                    <SafeIcon icon={FiIcons.FiChevronRight} className="text-xl" />
                </button>
            </div>

            {/* Bulk Actions Bar */}
            {selectedTasks.length > 0 && (
                <div className="fixed bottom-24 md:bottom-10 left-1/2 -translate-x-1/2 z-40 bg-[#1e3a8a] text-white px-6 py-3 rounded-full shadow-xl flex items-center space-x-4 animate-fade-in-up">
                    <span className="text-sm font-bold">{selectedTasks.length} selected</span>
                    <div className="h-4 w-px bg-blue-400"></div>
                    <button onClick={() => handleBatchSow(selectedTasks)} className="flex items-center space-x-2 text-xs font-bold hover:text-green-300 transition-colors">
                        <SafeIcon icon={FiCheckCircle} /> <span>Sow All</span>
                    </button>
                    <button onClick={() => handleBatchDelete(selectedTasks)} className="flex items-center space-x-2 text-xs font-bold hover:text-red-300 transition-colors">
                        <SafeIcon icon={FiTrash2} /> <span>Delete</span>
                    </button>
                    <div className="h-4 w-px bg-blue-400"></div>
                    <button onClick={() => setSelectedTasks([])} className="text-xs font-medium text-blue-200 hover:text-white">
                        Clear
                    </button>
                </div>
            )}

            <div className="flex justify-end mb-6">
                <button
                    onClick={() => setIsModalOpen(true)}
                    className="flex items-center space-x-2 bg-[#16a34a] hover:bg-green-700 text-white px-5 py-2.5 rounded-xl font-bold text-sm transition-all shadow-lg shadow-green-100"
                >
                    <SafeIcon icon={FiPlus} />
                    <span>Manual Task</span>
                </button>
            </div>

            {/* Sections */}
            {(microgreensToSoak.length + microgreensToSow.length + microgreensToUnveil.length + plantsToSow.length + plantsToTransplant.length) === 0 ? (
                <div className="text-center py-16 bg-white rounded-3xl border border-dashed border-gray-200">
                    <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-300">
                        <SafeIcon icon={FiCheckCircle} className="text-3xl" />
                    </div>
                    <h3 className="text-gray-900 font-bold mb-1">All Caught Up!</h3>
                    <p className="text-gray-400 text-sm">No operations scheduled for this date.</p>
                </div>
            ) : (
                <>
                    <TaskList title="1. Microgreens to Soak" tasks={microgreensToSoak} type="soak" />
                    <TaskList title="2. Microgreens to Do" tasks={microgreensToSow} type="sow" />
                    <TaskList title="3. Microgreens to Unveil" tasks={microgreensToUnveil} type="unveil" />
                    <TaskList title="4. Plants to Sow" tasks={plantsToSow} type="sow" />
                    <TaskList title="5. Plants to Transplant" tasks={plantsToTransplant} type="transplant" />
                </>
            )}

        </div>
    );
};

export default SowingManager;
